import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Person,
  ExitToApp,
  Home,
  AdminPanelSettings,
  Brightness4,
  Brightness7,
  Email,
  Help,
  ManageAccounts,
} from "@mui/icons-material";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getUserDisplayName } from "../utils/userUtils";
import { useThemeMode } from "../providers/themeProvider";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../features/auth/authSlice";
import { getUserProfile } from "../features/userProfile/userProfileSlice";
import { useAccessControl } from "../context/AccessControlContext";
import { getProfileCompletion } from "../utils/profileCompletion";
import ProfileCompletionDialog from "../components/ProfileCompletionDialog";
import { lookupPlayerByBsa } from "../features/player/playerSlice";
import {
  startImpersonation,
  stopImpersonation,
} from "../features/impersonation/impersonationSlice";

// Set only by the auto-open, so the popup appears once per session rather
// than on every page load. Cleared on logout so a different user on the same
// tab still gets it.
const POPUP_SHOWN_KEY = "profile_popup_shown";

const Header = () => {
  const { isAuthenticated, user, isLoading, logout } = useKindeAuth();

  const dispatch = useDispatch();
  const { isAdmin } = useAccessControl();

  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const profile = useSelector((state) => state.userProfile.profile);
  const profileLoading = useSelector((state) => state.userProfile.isLoading);
  const impersonatedPlayer = useSelector(
    (state) => state.impersonation.player,
  );

  // Admin impersonation dialog state
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [impersonateBsa, setImpersonateBsa] = useState("");
  const [impersonateError, setImpersonateError] = useState("");
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  // Load the profile once for the completion strip and the avatar initials.
  // Guarded so it skips when another page (e.g. My Record) already fetched it.
  useEffect(() => {
    if (isAuthenticated && !profile && !profileLoading) {
      dispatch(getUserProfile());
    }
  }, [isAuthenticated, profile, profileLoading, dispatch]);

  // MUI's Avatar renders the <img> the moment src is set (even while the
  // image is still loading) and only falls back to the initials after an
  // image error. So a picture that hangs, 404s silently, or loads a blank
  // placeholder leaves an empty circle. Preload the picture ourselves and
  // only pass it to the Avatar once it has loaded with real, visible
  // content - until then, and on any failure, the initials stay visible.
  const [pictureReady, setPictureReady] = useState(false);

  useEffect(() => {
    setPictureReady(false);

    const picture = user?.picture;
    if (!picture) return;

    const img = new Image();
    // Load with CORS so the pixels can be inspected below; providers that
    // don't send CORS headers fail here and simply keep the initials.
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;

        // A blank placeholder (e.g. a transparent default avatar) has no
        // visible pixels; a flat/white square has very few distinct
        // colours. Only accept the picture when it looks like content.
        const colors = new Set();
        let opaquePixels = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] === 0) continue; // transparent
          opaquePixels += 1;
          colors.add(`${data[i]},${data[i + 1]},${data[i + 2]}`);
        }
        setPictureReady(opaquePixels > 0 && colors.size > 8);
      } catch {
        // Canvas inspection failed (unusual) - keep the initials.
        setPictureReady(false);
      }
    };
    img.onerror = () => setPictureReady(false);
    img.src = picture;
  }, [user?.id, user?.picture]);

  const completion = getProfileCompletion(profile, user);

  // Auto-open the popup once per session when the profile is incomplete.
  useEffect(() => {
    if (!profile || profileLoading || completion.complete) return;

    if (!window.sessionStorage.getItem(POPUP_SHOWN_KEY)) {
      window.sessionStorage.setItem(POPUP_SHOWN_KEY, "1");
      setProfileDialogOpen(true);
    }
  }, [profile, profileLoading, completion.complete]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleClose();
  };

  // Admin impersonation: look the BSA up (never creates a player), then
  // switch the My Ranking page to the impersonated player's record.
  const handleImpersonateSubmit = async () => {
    const bsa = impersonateBsa.trim();
    if (!bsa) return;

    setImpersonateLoading(true);
    setImpersonateError("");
    const result = await dispatch(lookupPlayerByBsa(bsa));
    setImpersonateLoading(false);

    if (result.type === lookupPlayerByBsa.fulfilled.type) {
      const player = result.payload?.data || null;
      dispatch(
        startImpersonation({
          bsa_number: player.bsa_number,
          firstName: player.firstName,
          lastName: player.lastName,
        }),
      );
      setImpersonateDialogOpen(false);
      setImpersonateBsa("");
      navigate("/my-record");
    } else {
      setImpersonateError(
        result.payload?.message ||
          "No player found with that BSA number",
      );
    }
  };

  const handleImpersonateClose = () => {
    setImpersonateDialogOpen(false);
    setImpersonateBsa("");
    setImpersonateError("");
  };

  const handleStopImpersonation = () => {
    dispatch(stopImpersonation());
    handleClose();
  };

  const handleLogout = async () => {
    // A different user on the same tab should get the popup again, and
    // must never inherit an impersonation from the previous session.
    window.sessionStorage.removeItem(POPUP_SHOWN_KEY);
    dispatch(clearUser());
    dispatch(stopImpersonation());
    logout();
  };

  const getUserInitials = () => {
    const firstName = profile?.first_name || user?.givenName || user?.given_name;
    const lastName = profile?.last_name || user?.familyName || user?.family_name;

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }

    // Fall back to full name parsing
    const name = getUserDisplayName(user, profile);
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    const fallback = name.charAt(0).toUpperCase();
    return fallback || "?";
  };

  if (isLoading) return null; // Don't render header while checking auth

  const showCompletionStrip =
    isAuthenticated && profile && !completion.complete;

  return (
    <>
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              onClick={() => navigate(isAuthenticated ? "/my-record" : "/")}
              sx={{
                cursor: "pointer",
                "&:hover": { opacity: 0.8 },
              }}
            >
              PBA Ranking System
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Contact Email Button */}
            <Button
              color="inherit"
              startIcon={<Email />}
              href="mailto:support@bowlsmaster.co.za?subject=PBA%20Ranking%20Query"
              sx={{ display: { xs: "none", sm: "inline-flex" }, px: 2 }}
              data-tutorial="contact-email"
            >
              support@bowlsmaster.co.za
            </Button>

            {/* Theme Toggle Button */}
            <IconButton
              onClick={toggleTheme}
              color="inherit"
              title={
                mode === "light" ? "Switch to dark mode" : "Switch to light mode"
              }
              data-tutorial="theme-toggle"
            >
              {mode === "light" ? <Brightness4 /> : <Brightness7 />}
            </IconButton>

            {isAuthenticated ? (
              <>
                <Button
                  color="inherit"
                  startIcon={<Person />}
                  onClick={() => navigate("/my-record")}
                  sx={{
                    display: "flex",
                    fontSize: { xs: "0.75rem", sm: "1rem" },
                  }}
                  data-tutorial="my-ranking-button"
                >
                  My Ranking
                </Button>
                <Button
                  color="inherit"
                  startIcon={<Home />}
                  onClick={() => navigate("/pba-log")}
                  sx={{
                    display: "flex",
                    fontSize: { xs: "0.75rem", sm: "1rem" },
                  }}
                  data-tutorial="home-button"
                >
                  PBA Log
                </Button>
                {/* {isAdmin && (
                  <Button
                    color="inherit"
                    startIcon={<People />}
                    onClick={() => navigate("/players")}
                    sx={{ display: { xs: "none", sm: "flex" } }}
                  >
                    Players
                  </Button>
                )} */}

                {/* User Menu */}
                <IconButton
                  size="large"
                  onClick={handleMenu}
                  color="inherit"
                  sx={{ ml: 1 }}
                  data-tutorial="profile-menu"
                >
                  <Avatar
                    src={pictureReady ? user?.picture : undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      // The AppBar fills with primary.main, so painting the
                      // circle the same colour made it invisible against the
                      // header. Use the paper surface instead so the avatar
                      // stands out in both themes.
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                    alt={getUserDisplayName(user, profile)}
                  >
                    {/* The picture is only passed in once it has loaded
                        with visible content (see the pictureReady preload
                        above), so these children - the user's initials
                        from their first and last name - are shown until
                        then, and remain shown if the picture is missing,
                        blank, or fails to load. */}
                    {getUserInitials()}
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  PaperProps={{
                    sx: { mt: 1, minWidth: 200 },
                  }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Signed in as
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {getUserDisplayName(user, profile)}
                    </Typography>
                  </Box>
                  <Divider />

                  {/* Mobile navigation items */}
                  <MenuItem
                    onClick={() => handleMenuClick("/my-record")}
                    sx={{ display: { xs: "flex", sm: "none" } }}
                  >
                    <Person sx={{ mr: 2 }} />
                    My Ranking
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleMenuClick("/pba-log")}
                    sx={{ display: { xs: "flex", sm: "none" } }}
                  >
                    <Home sx={{ mr: 2 }} />
                    PBA Log
                  </MenuItem>

                  {/* {isAdmin && (
                    <MenuItem
                      onClick={() => handleMenuClick("/players")}
                      sx={{ display: { xs: "flex", sm: "none" } }}
                    >
                      <People sx={{ mr: 2 }} />
                      Players
                    </MenuItem>
                  )} */}

                  <Divider sx={{ display: { xs: "block", sm: "none" } }} />

                  <MenuItem
                    onClick={() => {
                      handleClose();
                      setProfileDialogOpen(true);
                    }}
                  >
                    <Person sx={{ mr: 2 }} />
                    My Profile
                  </MenuItem>
                  {isAdmin && (
                    <MenuItem onClick={() => handleMenuClick("/admin")}>
                      <AdminPanelSettings sx={{ mr: 2 }} />
                      Admin Actions
                    </MenuItem>
                  )}
                  {isAdmin && (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        setImpersonateBsa("");
                        setImpersonateError("");
                        setImpersonateDialogOpen(true);
                      }}
                    >
                      <ManageAccounts sx={{ mr: 2 }} />
                      Impersonate Player
                    </MenuItem>
                  )}
                  {isAdmin && impersonatedPlayer && (
                    <MenuItem onClick={handleStopImpersonation}>
                      <ExitToApp sx={{ mr: 2 }} />
                      Stop Impersonation
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      localStorage.removeItem("pba_tutorial_seen");
                      window.location.reload();
                    }}
                  >
                    <Help sx={{ mr: 2 }} />
                    Restart Tutorial
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      localStorage.removeItem("pba_flag_tutorial_seen");
                      window.location.reload();
                    }}
                  >
                    <Help sx={{ mr: 2 }} />
                    Restart Flag Tutorial
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleLogout()}>
                    <ExitToApp sx={{ mr: 2 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  color="inherit"
                  onClick={() => navigate("/login")}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  Login
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate("/signup")}
                  variant="contained"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.15)",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.25)" },
                  }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Box>
        </Toolbar>

        {/* Profile completion strip - visible until the profile is complete,
            then gone entirely. */}
        {showCompletionStrip && (
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 1,
              bgcolor: "rgba(255, 255, 255, 0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <LinearProgress
                  variant="determinate"
                  value={completion.pct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "rgba(255, 255, 255, 0.25)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: (theme) => theme.palette.background.default,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "inherit", opacity: 0.9, whiteSpace: "nowrap" }}
              >
                {completion.pct}% complete
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setProfileDialogOpen(true)}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  color: "inherit",
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Complete profile
              </Button>
            </Box>
          </Box>
        )}
      </AppBar>

      <ProfileCompletionDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />

      {/* Admin impersonation dialog - asks for the player's BSA number */}
      <Dialog
        open={impersonateDialogOpen}
        onClose={handleImpersonateClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Impersonate Player</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the player's BSA number to view their ranking page as if
            you were them.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="BSA Number"
            value={impersonateBsa}
            onChange={(e) => setImpersonateBsa(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !impersonateLoading) {
                handleImpersonateSubmit();
              }
            }}
            error={!!impersonateError}
            helperText={impersonateError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImpersonateClose} disabled={impersonateLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleImpersonateSubmit}
            variant="contained"
            disabled={impersonateLoading || !impersonateBsa.trim()}
          >
            {impersonateLoading ? "Looking up..." : "Impersonate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
